import React from "react";
import { ArrowRight } from "lucide-react";

/**
 * HeroCards
 *  - Mobile (< sm / 640px): clean stacked rounded cards with a full-width CTA at the bottom.
 *  - sm+ : the notched-SVG "claymorphism" cards with the button sitting in the L-shaped cutout.
 *
 * Same content, two layouts.
 */

const HOMEOWNER_AVATARS = [
  "https://images.unsplash.com/photo-1672462478040-a5920e2c23d8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHxzbWlsaW5nJTIwcGVyc29uJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzc3Mzk0OTU5fDA&ixlib=rb-4.1.0&q=85&w=160",
  "https://images.unsplash.com/photo-1635088047149-0d698fb5f849?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwzfHxzbWlsaW5nJTIwcGVyc29uJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzc3Mzk0OTU5fDA&ixlib=rb-4.1.0&q=85&w=160",
  "https://images.pexels.com/photos/30034933/pexels-photo-30034933.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=160&w=160",
];

const CONTRACTOR_AVATARS = [
  "https://images.unsplash.com/photo-1762522926157-bcc04bf0b10a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHw0fHxtYWxlJTIwcHJvZmVzc2lvbmFsJTIwaGVhZHNob3R8ZW58MHx8fHwxNzc3Mzk1OTQzfDA&ixlib=rb-4.1.0&q=85&w=160",
  "https://images.unsplash.com/photo-1649433658557-54cf58577c68?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwyfHxtYWxlJTIwcHJvZmVzc2lvbmFsJTIwaGVhZHNob3R8ZW58MHx8fHwxNzc3Mzk1OTQzfDA&ixlib=rb-4.1.0&q=85&w=160",
  "https://images.pexels.com/photos/37148308/pexels-photo-37148308.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=160&w=160",
];

/* ============================================================ */
/*  Mobile card (< sm)                                          */
/* ============================================================ */
const MobileCard = ({
  variant,
  title,
  description,
  trustText,
  avatars,
  image,
  ctaLabel,
  ctaHref,
}) => {
  const colors = variant === "orange"
    ? { stroke: "#F26B1F", fill: "#F26B1F", shadow: "rgba(242,107,31,0.45)" }
    : { stroke: "#1AA0DD", fill: "#1AA0DD", shadow: "rgba(26,160,221,0.45)" };

  return (
    <div
      className="relative w-full rounded-2xl overflow-hidden bg-white"
      style={{
        border: `2px solid ${colors.stroke}`,
        boxShadow: "0 12px 28px -14px rgba(15,23,42,0.18), 0 2px 4px rgba(15,23,42,0.05)",
      }}
    >
      <div className="p-5">
        <div className="flex items-start gap-4">
          <div className="flex-1 min-w-0">
            <h2 className="font-extrabold text-[#0f172a] tracking-tight text-[19px] leading-[1.15]">
              {title}
            </h2>
            <p className="mt-2 text-[#1e293b] text-[13px] leading-[1.5]">
              {description}
            </p>
          </div>
          {image && (
            <img
              src={image}
              alt=""
              loading="lazy"
              className="shrink-0 rounded-lg object-cover h-[80px] w-[80px] ring-1 ring-black/5"
            />
          )}
        </div>

        {/* Trust row */}
        <div className="mt-5 flex items-center gap-3">
          <div className="flex -space-x-2 shrink-0">
            {avatars.map((src, i) => (
              <img
                key={i}
                src={src}
                alt=""
                loading="lazy"
                className="h-8 w-8 rounded-full object-cover ring-2 ring-white shadow-sm"
              />
            ))}
          </div>
          <p className="text-[#0f172a] font-semibold text-[12px] leading-snug">
            {trustText}
          </p>
        </div>
      </div>

      {/* Full-width CTA bar */}
      <a
        href={ctaHref}
        className="group block w-full"
        aria-label={typeof ctaLabel === "string" ? ctaLabel : undefined}
      >
        <div
          className="relative flex items-center justify-center gap-2 px-5 py-3.5 overflow-hidden"
          style={{ backgroundColor: colors.fill }}
        >
          {/* shimmer */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 -left-1/2 w-1/2 -skew-x-12 bg-gradient-to-r from-transparent via-white/35 to-transparent transition-transform duration-700 ease-out group-hover:translate-x-[300%]"
          />
          <span className="relative text-white font-bold text-[14.5px] drop-shadow-[0_1px_2px_rgba(0,0,0,0.25)]">
            {ctaLabel}
          </span>
          <ArrowRight className="relative h-4 w-4 text-white transition-transform duration-300 group-hover:translate-x-0.5" strokeWidth={3} />
        </div>
      </a>
    </div>
  );
};

/* ============================================================ */
/*  Desktop card (sm+)                                          */
/* ============================================================ */
const DesktopCard = ({
  variant = "blue",
  title,
  description,
  trustText,
  avatars,
  ctaLabel,
  ctaHref = "#",
  image,
}) => {
  const COLORS = {
    blue: { stroke: "#1AA0DD", fill: "#1AA0DD" },
    orange: { stroke: "#F26B1F", fill: "#F26B1F" },
  };
  const C = COLORS[variant];
  const STROKE = 3;
  const cardW = 540;
  const cardH = 220;
  const btnW = 200;
  const btnH = 64;
  const gap = 7;
  const R = 20;
  const r = 11;
  const notchX = cardW - btnW - gap;
  const notchY = cardH - btnH - gap;
  const btnX = cardW - btnW;
  const btnY = cardH - btnH;

  const cardPath = [
    `M ${R} 0`,
    `H ${cardW - R}`,
    `A ${R} ${R} 0 0 1 ${cardW} ${R}`,
    `V ${notchY - r}`,
    `A ${r} ${r} 0 0 1 ${cardW - r} ${notchY}`,
    `H ${notchX + r}`,
    `A ${r} ${r} 0 0 0 ${notchX} ${notchY + r}`,
    `V ${cardH}`,
    `H ${R}`,
    `A ${R} ${R} 0 0 1 0 ${cardH - R}`,
    `V ${R}`,
    `A ${R} ${R} 0 0 1 ${R} 0`,
    `Z`,
  ].join(" ");

  return (
    <div className="w-full">
      <div
        className="relative w-full"
        style={{ aspectRatio: `${cardW} / ${cardH}`, minHeight: "180px" }}
      >
        <svg
          viewBox={`0 0 ${cardW} ${cardH}`}
          className="absolute inset-0 w-full h-full overflow-visible"
          preserveAspectRatio="none"
          aria-hidden="true"
          style={{
            filter: `
              drop-shadow(0 1px 0 rgba(255,255,255,0.9))
              drop-shadow(0 12px 18px rgba(15,23,42,0.08))
              drop-shadow(0 28px 40px rgba(15,23,42,0.06))
              drop-shadow(0 4px 6px rgba(15,23,42,0.05))
            `,
          }}
        >
          <path
            d={cardPath}
            fill="#ffffff"
            stroke={C.stroke}
            strokeWidth={STROKE}
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        </svg>

        <div
          className="absolute left-0 top-0"
          style={{ width: `${(notchX / cardW) * 100}%`, height: "100%" }}
        >
          <div className="h-full flex flex-col justify-between p-5 sm:p-6">
            <div>
              <h2 className="font-extrabold text-[#0f172a] tracking-tight text-[19px] sm:text-[20px] leading-[1.15]">
                {title}
              </h2>
              <p className="mt-2 text-[#1e293b] text-[12.5px] sm:text-[13px] leading-[1.5]">
                {description}
              </p>
            </div>
          </div>
        </div>

        {image && (
          <img
            src={image}
            alt=""
            loading="lazy"
            className="absolute rounded-lg object-cover h-[88px] w-[120px] sm:h-[100px] sm:w-[140px] ring-1 ring-black/5 z-10"
            style={{ top: "20px", right: "24px" }}
          />
        )}

        <div
          className="absolute"
          style={{ left: 0, bottom: 0, width: `${(notchX / cardW) * 100}%` }}
        >
          <div className="flex items-center gap-2.5 p-5 sm:p-6">
            <div className="flex -space-x-2 shrink-0">
              {avatars.map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt=""
                  loading="lazy"
                  className="h-8 w-8 sm:h-9 sm:w-9 rounded-full object-cover ring-2 ring-white shadow-sm"
                />
              ))}
            </div>
            <p className="text-[#0f172a] font-semibold text-[11.5px] sm:text-[12px] leading-snug">
              {trustText}
            </p>
          </div>
        </div>

        <a
          href={ctaHref}
          aria-label={typeof ctaLabel === "string" ? ctaLabel : undefined}
          className="absolute group"
          style={{
            left: `${(btnX / cardW) * 100}%`,
            top: `${(btnY / cardH) * 100}%`,
            width: `${(btnW / cardW) * 100}%`,
            height: `${(btnH / cardH) * 100}%`,
          }}
        >
          <div
            className="relative w-full h-full flex items-center justify-center rounded-[10px] overflow-hidden transition-all duration-300 ease-out group-hover:-translate-y-0.5 group-active:translate-y-0"
            style={{
              backgroundColor: C.fill,
              border: "2.5px solid #ffffff",
              boxShadow: "0 6px 14px rgba(0, 0, 0, 0.18), 0 2px 4px rgba(0, 0, 0, 0.12)",
            }}
          >
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 -left-1/2 w-1/2 -skew-x-12 bg-gradient-to-r from-transparent via-white/35 to-transparent transition-transform duration-700 ease-out group-hover:translate-x-[300%]"
            />
            <span className="relative flex items-center gap-2 text-white font-bold text-[14px] sm:text-[15px] text-center leading-tight px-3 drop-shadow-[0_1px_2px_rgba(0,0,0,0.25)]">
              <span className="transition-transform duration-300 group-hover:-translate-x-0.5">
                {ctaLabel}
              </span>
              <ArrowRight
                className="w-0 h-4 opacity-0 -translate-x-2 transition-all duration-300 group-hover:w-4 group-hover:opacity-100 group-hover:translate-x-0"
                strokeWidth={3}
              />
            </span>
          </div>
        </a>
      </div>
    </div>
  );
};

/* ============================================================ */
/*  Wrapper that picks layout based on viewport                  */
/* ============================================================ */
const HeroCards = () => {
  const homeowner = {
    variant: "blue",
    title: "Are You A Home Owner?",
    description:
      "See your dream home before it exists. Instantly designed, priced, and ready to build on your schedule.",
    image:
      "https://images.unsplash.com/photo-1580587771525-78b9dba3b914?auto=format&fit=crop&w=240&h=240&q=80",
    trustText: (
      <>
        Trusted By 5k+ Home <br />
        Owners Like You
      </>
    ),
    avatars: HOMEOWNER_AVATARS,
    ctaLabel: "Get a Quote",
    ctaHref: "#quote",
  };

  const contractor = {
    variant: "orange",
    title: "Are You A Home Contractor?",
    description: (
      <>
        Stop chasing leads and receive qualified jobs in your area, delivered
        directly to you!{" "}
        <span className="font-bold text-[#F26B1F]">No</span> marketing,{" "}
        <span className="font-bold text-[#F26B1F]">No</span> Sales
      </>
    ),
    image:
      "https://images.unsplash.com/photo-1679797870465-b4eda40ead96?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxjb250cmFjdG9yJTIwd29ya2luZ3xlbnwwfHx8fDE3Nzc2MDY4OTR8MA&ixlib=rb-4.1.0&q=85&w=400",
    trustText: (
      <>
        Trusted By 15k+ Home <br />
        Professionals Like You
      </>
    ),
    avatars: CONTRACTOR_AVATARS,
    ctaLabel: "Yes! Show Me How!",
    ctaHref: "#contractor",
  };

  return (
    <section className="relative w-full pt-10 pb-10 sm:pt-16 lg:pt-20 lg:pb-12 overflow-hidden">
      {/* Full-width grey semi-circle backdrop with dark stroke */}
      <div
        aria-hidden="true"
        className="absolute inset-x-0 bottom-0 pointer-events-none"
        style={{ height: "92%" }}
      >
        <svg
          className="w-full h-full"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <ellipse
            cx="50"
            cy="105"
            rx="62"
            ry="100"
            fill="#EEF2F6"
            stroke="#94A3B8"
            strokeWidth="2"
            vectorEffect="non-scaling-stroke"
          />
        </svg>
      </div>

      <div className="relative max-w-[1200px] mx-auto px-4">
        {/* Mobile layout */}
        <div className="grid grid-cols-1 gap-5 sm:hidden">
          <MobileCard {...homeowner} />
          <MobileCard {...contractor} />
        </div>

        {/* sm+ layout */}
        <div className="hidden sm:grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10">
          <DesktopCard {...homeowner} />
          <DesktopCard {...contractor} />
        </div>
      </div>
    </section>
  );
};

export default HeroCards;
